import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  Bindable,
  amorphicStatic,
} from '@haventech/amorphic';
import { CallContext, PreServerCallChanges } from '@haventech/amorphic/dist/lib/types/HelperTypes';
import { HTTPObjs, RemoteCall } from '@haventech/amorphic/dist/lib/types/ILifecycleController';

import {
  defaultEmail,
  defaultPassword,
  defaultAdminRole,
  deferEmailChange,
  passwordExpiresMinutes,
  queryFilter,
  validateEmail,
  validateEmailAndLogin,
  insertFilter,
  log,
  temporaryPasswordExpiresMinutes,
} from './AuthenticationHelpers';

import * as urlparser from 'url';

import { SecurityContext } from './SecurityContext';
import { Principal } from './Principal';
import { Policy } from './customer/Policy';
import { Person } from './customer/Person';
import { Party } from './customer/Party';

import { ElasticSchema } from '@hit/hit-schemas';
import { RabbitmqPublisher } from '../../common/js/rabbitmqPublisher/RabbitmqPublisher';
import { EmailerUtils } from './emails/EmailerUtils';

import { Auth0Client, Auth0ResponseAccount } from './authentication/Auth0Client';
import { Customer } from './customer/Customer';
import { serverOnly } from './helpers';
import {
  agentToAdvisor,
  getAdditionalAdvisors,
  getInsured,
  getJuvieSigner,
  getOtherOwner,
  getOwner,
  PersonElastic,
} from './elastic/ElasticUtils';
import { AdvisorInfoElastic } from './elastic/AdvisorInfoElastic';
import { CPLogger, Category, PiiLevelEnum } from './logger/CPLogger';
import { LoggerSingleton } from './logger/LoggerSingleton';

@supertypeClass
export abstract class AuthenticatingController extends Bindable(Remoteable(Persistable(Supertype))) {
  @property({ length: 50, rule: ['name', 'required'] })
  firstName: string = '';

  @property({ length: 50, rule: ['name', 'required'] })
  lastName: string = '';

  @property({ length: 50, rule: ['email', 'required'] })
  email: string = '';

  @property({ length: 50, rule: ['email', 'required'] })
  newEmail: string = '';

  // Secure variables never leaked to the client

  @property({ toClient: false, sensitiveData: true })
  password: string = '';

  @property({ toClient: false, rule: ['required'], sensitiveData: true })
  confirmPassword: string = '';

  @property({ toClient: false, rule: ['required'], sensitiveData: true })
  newPassword: string = '';

  @property({ toClient: false, sensitiveData: true })
  passwordChangeHash: string = '';

  @property({ toClient: false })
  verifyEmailCode: string = '';

  @property({ toServer: false })
  loggedIn: boolean = false;

  @property({ toServer: false })
  loggedInRole: string;

  @property()
  elasticSend: boolean = false;

  @property({ toClient: true, toServer: false })
  isAuth0Enabled: boolean = null;

  @property({ toClient: true, toServer: false })
  isAccountVerified: boolean = false;

  @property()
  enableNewReplacementFeature: boolean = true;

  constructor() {
    super();
  }

  init(): this {
    return this;
  }

  isAdmin(): boolean {
    return this.securityContext.isAdmin();
  }

  @property({ toServer: false })
  securityContext: SecurityContext;

  abstract setPrincipal(principal: Principal);
  abstract getPrincipal(): Principal;
  abstract newPrincipal(): Principal;

  isLoggedIn(): boolean {
    return !!this.loggedIn;
  }

  @serverOnly()
  async createAdmin(): Promise<void> {
    const principal = this.getPrincipal();
    const count = await principal.amorphicClass.countFromPersistWithQuery({ role: defaultAdminRole.call(this) });
    if (count === 0) {
      const admin = this.newPrincipal();
      admin.email = defaultEmail.call(this) || 'amorphic@amorphic.com';
      admin.firstName = 'Admin';
      admin.lastName = 'User';
      admin.role = defaultAdminRole.call(this);
      admin.establishPassword(defaultPassword.call(this) || 'admin', null, true, true);
      this.amorphicate(admin);
    }
  }

  /**
   * Create a new principal if one does not exist. This method is used by the currently logged in user to create
   * new users. The principal info comes from the an object which should have the following properties:
   *
   * firstName, lastName, email, newPassword, confirmPassword, role
   *
   * Also used to reset a password
   */
  @remote({
    validate: function () {
      return this.validate(document.getElementById('publicRegisterFields'));
    },
  })
  createNewAdmin(adminUser, url, pageConfirmation?, pageInstructions?, reset?): Promise<Principal> {
    // Check for security context of security admin
    if (this.loggedInRole !== defaultAdminRole.call(this)) {
      throw { code: 'cannotcreateadmin', text: 'Only a security admin can create users' };
    }
    if (adminUser.newPassword !== adminUser.confirmPassword) {
      throw { code: 'passwordmismatch', text: 'Passwords are not the same' };
    }

    let principal;

    url = url ? urlparser.parse(url, true) : '';
    return this.getPrincipal()
      .amorphicClass.getFromPersistWithQuery({ email: adminUser.email })
      .then(
        function (principals) {
          if (reset) {
            if (principals.length === 0) {
              throw { code: 'email_notfound', text: "Can't find this user" };
            }
            principal = principals[0];
          } else {
            if (principals.length > 0) {
              throw { code: 'email_registered', text: 'This email is already registered' };
            }
            principal = this.newPrincipal();
          }

          // this[principalProperty] = this[principalProperty] || Principal.new();
          principal.lockedOut = false;
          if (!reset) {
            principal.email = adminUser.email;
            principal.firstName = adminUser.firstName;
            principal.lastName = adminUser.lastName;
            principal.role = adminUser.role;
          }
          return principal.establishPassword(
            adminUser.newPassword,
            principal.role === defaultAdminRole.call(this)
              ? null
              : new Date(new Date().getTime() + temporaryPasswordExpiresMinutes.call(this) * 1000 * 60),
            false,
            true
          );
        }.bind(this)
      )
      .then(
        function () {
          if (validateEmail.call(this)) {
            this.amorphicate(principal);
            return principal.setEmailVerificationCode();
          } else {
            return Promise.resolve();
          }
        }.bind(this)
      )
      .then(
        function () {
          if (url) {
            this.sendEmail(
              validateEmail.call(this) ? 'register_verify' : 'register',
              principal.email,
              this.firstName + ' ' + this.lastName,
              [
                { name: 'firstName', content: this.firstName },
                { name: 'email', content: this.email },
                {
                  name: 'link',
                  content:
                    url.protocol +
                    '//' +
                    url.host.replace(/:.*/, '') +
                    (url.port > 1000 ? ':' + url.port : '') +
                    '?email=' +
                    encodeURIComponent(this.email) +
                    '&code=' +
                    principal.validateEmailCode +
                    '#verify_email',
                },
              ]
            );
          }
          if (validateEmail.call(this) && pageInstructions) {
            return this.setPage(pageInstructions);
          }
          if (!validateEmail.call(this) && pageConfirmation) {
            return this.setPage(pageConfirmation);
          }
          return Promise.resolve(principal);
        }.bind(this)
      );
  }

  /**
   * Create a new principal if one does not exist and consider ourselves logged in
   *
   * @param password
   */
  @remote({
    validate: function () {
      return this.validate(document.getElementById('publicRegisterFields'));
    },
  })
  publicRegister(url, pageConfirmation?, pageInstructions?): Promise<void> {
    if (this.loggedIn) {
      throw { code: 'already_loggedin', text: 'Already logged in' };
    }

    this.getPrincipal().validateNewPassword(this.newPassword);

    if (this.newPassword !== this.confirmPassword) {
      throw { code: 'passwordmismatch', text: 'Passwords are not the same' };
    }

    let principal;

    url = urlparser.parse(url, true);
    return this.getPrincipal()
      .amorphicClass.countFromPersistWithQuery(
        queryFilter.call(this, {
          email: {
            $regex: new RegExp('^' + this.email.toLowerCase().replace(/([^0-9a-zA-Z])/g, '\\$1') + '$'),
            $options: 'i',
          },
        })
      )
      .then(
        function (count) {
          if (count > 0) {
            throw { code: 'email_registered', text: 'This email is already registered' };
          }

          return this.preRegistrationCheck();
        }.bind(this)
      )
      .then((preRegistrationCheck) => {
        if (preRegistrationCheck) {
          throw {
            code: preRegistrationCheck.code,
            text: preRegistrationCheck.text,
          };
        }

        this.setPrincipal(this.getPrincipal() || this.newPrincipal());
        principal = this.getPrincipal();
        principal.email = this.email;
        principal.firstName = this.firstName;
        principal.lastName = this.lastName;
        insertFilter.call(this, principal);

        return principal.establishPassword(this.newPassword);
      })
      .then(
        function () {
          if (validateEmail.call(this)) {
            return principal.setEmailVerificationCode();
          } else {
            return Promise.resolve(true);
          }
        }.bind(this)
      )
      .then(
        function () {
          if (!validateEmail.call(this) || validateEmailAndLogin.call(this)) {
            this.amorphicate(principal);
            this.setLoggedInState(principal);
          }

          LoggerSingleton.getInstance().info({
            module: 'AuthenticatingController',
            function: 'publicRegister',
            category: Category.milestone,
            context: { piiLevel: PiiLevelEnum.s2 },
            data: {
              isAuth0Enabled: this.isAuth0Enabled,
              email: principal.email,
            },
          });

          if (!this.isAuth0Enabled) {
            this.sendEmail(
              validateEmail.call(this) ? 'register_verify' : 'register',
              principal.email,
              principal.firstName + ' ' + principal.lastName,
              [
                { name: 'firstName', content: this.firstName },
                { name: 'email', content: this.email },
                {
                  name: 'link',
                  content:
                    url.protocol +
                    '//' +
                    url.host.replace(/:.*/, '') +
                    (url.port > 1000 ? ':' + url.port : '') +
                    '?email=' +
                    encodeURIComponent(principal.email) +
                    '&code=' +
                    principal.validateEmailCode +
                    '#verify_email',
                },
                { name: 'verificationCode', content: this.getPrincipal().validateEmailCode },
              ]
            );
          }

          if (validateEmail.call(this) && pageInstructions) {
            return this.setPage(pageInstructions);
          }
          if (!validateEmail.call(this) && pageConfirmation) {
            return this.setPage(pageConfirmation);
          }
        }.bind(this)
      );
  }

  protected async preRegistrationCheck(): Promise<PreRegistrationVerificationResult> {
    return null;
  }

  /**
   * login the user
   */
  @remote({
    validate: function () {
      return this.validate(document.getElementById('publicLoginFields'));
    },
  })
  async publicLogin(isCoverpath: boolean): Promise<void> {
    if (this.loggedIn) {
      throw { code: 'already_loggedin', text: 'Already logged in' };
    }

    if (!this.email) {
      throw { code: 'invalid email', text: 'email is null or empty' };
    }

    const principals = await this.getPrincipal().amorphicClass.persistorFetchByQuery({
      _template: isCoverpath ? 'CoverpathCustomer' : { $ne: 'CoverpathCustomer' },
      email: {
        $regex: new RegExp('^' + this.email.toLowerCase().replace(/([^0-9a-zA-Z])/g, '\\$1') + '$'),
        $options: 'i',
      },
    });

    if (principals.length === 0 || principals[0].suspended) {
      log.call(this, 'Log In attempt for ' + this.email + ' failed (invalid email)');
      throw { code: 'invalid_email_or_password', text: 'Incorrect email or password' };
    }
    let principal = principals[0];

    await principal.authenticate(this.password);

    principal = await this.getPrincipal().amorphicClass.persistorFetchById(principal._id);

    const forceChange = !!principal.mustChangePassword;
    if (forceChange && !this.newPassword) {
      throw { code: 'changePassword', text: 'Please change your password' };
    }

    const status = forceChange ? await this.changePasswordForPrincipal(principal) : true;
    if (status) {
      this.setLoggedInState(principal);
      this.amorphicate(principal);
    }
  }

  /**
   * login the user with changed email. Also verify email code
   */
  @remote({
    validate: function () {
      return this.validate(document.getElementById('publicLoginFields'));
    },
  })
  publicLoginWithNewEmail(page?) {
    let principal;

    return this.getPrincipal()
      .amorphicClass.getFromPersistWithQuery(
        queryFilter.call(this, {
          newEmail: {
            $regex: new RegExp('^' + this.email.toLowerCase().replace(/([^0-9a-zA-Z])/g, '\\$1') + '$'),
            $options: 'i',
          },
        }),
        null,
        null,
        null,
        true
      )
      .then(
        function (principals) {
          if (principals.length === 0) {
            log.call(this, 'Log In attempt for ' + this.email + ' failed (invalid email)');
            throw { code: 'invalid_email_or_password', text: 'Incorrect email or password' };
          }
          principal = principals[0];
          return principal.authenticate(this.password);
        }.bind(this)
      )
      .then(
        function () {
          return this.getPrincipal().amorphicClass.persistorFetchById(principal._id);
        }.bind(this)
      )
      .then(
        function (p) {
          principal = p;
          if (principal.mustChangePassword && !this.newPassword) {
            throw { code: 'changePassword', text: 'Please change your password' };
          }
          return principal.mustChangePassword ? this.changePasswordForPrincipal(principal) : Promise.resolve(true);
        }.bind(this)
      )
      .then(
        function (status) {
          return principal.consumeEmailVerificationCode(this.verifyEmailCode);
        }.bind(this)
      )
      .then(
        function () {
          this.setLoggedInState(principal);
          this.amorphicate(principal);

          principal.email = this.email;
          principal.newEmail = ''; // No need to track the changed email anymore
          principal.persistSave();

          // Send an email changed confirmation email
          this.sendEmail(
            'confirm_emailchange',
            this.email,
            principal.email,
            principal.firstName + ' ' + principal.lastName,
            [
              { name: 'email', content: this.email },
              { name: 'firstName', content: principal.firstName },
            ]
          );

          return page ? this.setPage(page) : Promise.resolve(true);
        }.bind(this)
      );
  }

  /**
   *  Set up all fields to indicate logged in
   */
  setLoggedInState(principal) {
    this.loggedIn = true;
    this.loggedInRole = principal.role;
    this.setPrincipal(principal);
    // One way so you can't spoof from client
    this.securityContext = SecurityContext.new(principal, principal.role);
  }

  /**
   *  Set up all fields to indicate logged out
   */
  setLoggedOutState() {
    this.setPrincipal(null);
    this.loggedIn = false;
    this.loggedInRole = null;
    this.securityContext = null;
  }

  /*
   * logout the current user
   */
  @remote({ public: true })
  publicLogout(page?) {
    log.call(this, 'Customer ' + this.lastName + ' logged out');
    this.setLoggedOutState();
    return page ? this.setPage(page) : null;
  }

  @remote({ on: 'client' })
  setPage(page): void {
    // should be overriddent if you want to go to a page
  }

  /**
   * change an email address for a logged in user
   */
  @remote({
    validate: function () {
      return this.validate(document.getElementById('changeEmailFields'));
    },
  })
  async changeEmail(page, url) {
    url = urlparser.parse(url, true);
    const principal = this.getPrincipal();
    const oldEmail = principal.email;
    const newEmail = this.newEmail;

    return principal
      .authenticate(this.password, null, true)
      .then(
        function () {
          return this.getPrincipal().amorphicClass.countFromPersistWithQuery(
            queryFilter.call(this, {
              email: {
                $regex: new RegExp('^' + newEmail.toLowerCase().replace(/([^0-9a-zA-Z])/g, '\\$1') + '$'),
                $options: 'i',
              },
            })
          );
        }.bind(this)
      )
      .then(
        function (count) {
          if (count > 0) {
            throw { code: 'email_registered', text: 'This email is already registered' };
          }
        }.bind(this)
      )
      .then(
        function () {
          if (validateEmail.call(this)) {
            return principal.setEmailVerificationCode();
          } else {
            return Promise.resolve(false);
          }
        }.bind(this)
      )
      .then(
        function () {
          if (!deferEmailChange.call(this)) {
            this.email = newEmail;
          }

          principal.newEmail = newEmail;
          principal.persistSave();

          // Send an email to old email address which is purely informational
          this.sendEmail('email_changed', oldEmail, principal.email, principal.firstName + ' ' + principal.lastName, [
            { name: 'oldEmail', content: oldEmail },
            { name: 'email', content: newEmail },
            { name: 'firstName', content: principal.firstName },
          ]);

          // Send an email to new email address asking to verify the new email
          // address
          this.sendEmail(
            validateEmail.call(this) ? 'email_changed_verify' : 'email_changed',
            newEmail,
            principal.firstName + ' ' + principal.lastName,
            [
              { name: 'oldEmail', content: oldEmail },
              { name: 'email', content: newEmail },
              { name: 'firstName', content: principal.firstName },
              {
                name: 'link',
                content:
                  url.protocol +
                  '//' +
                  url.host.replace(/:.*/, '') +
                  (url.port > 1000 ? ':' + url.port : '') +
                  '?email=' +
                  encodeURIComponent(newEmail) +
                  '&code=' +
                  principal.validateEmailCode +
                  (deferEmailChange.call(this) ? '#verify_email_change' : '#verify_email'),
              },
              { name: 'verificationCode', content: principal.validateEmailCode },
            ]
          );

          log.call(this, 'Changed email ' + oldEmail + ' to ' + newEmail);
          return page ? this.setPage(page) : Promise.resolve(true);
        }.bind(this)
      );
  }

  abstract sendEmail(slug, email, name, emails: Array<any>);

  @remote({
    validate: function () {
      return this.validate(document.getElementById('changeEmailFields'));
    },
  })
  resendChangeEmailValidationCode(url) {
    url = urlparser.parse(url, true);
    const principal = this.getPrincipal();
    this.sendEmail('email_verify', principal.email, principal.firstName + ' ' + principal.lastName, [
      { name: 'email', content: principal.email },
      { name: 'firstName', content: principal.firstName },
      {
        name: 'link',
        content:
          url.protocol +
          '//' +
          url.host.replace(/:.*/, '') +
          (url.port > 1000 ? ':' + url.port : '') +
          '?email=' +
          encodeURIComponent(this.email) +
          '&code=' +
          principal.validateEmailCode +
          '#verify_email',
      },
      { name: 'verificationCode', content: this.getPrincipal().validateEmailCode },
    ]);

    log.call(this, 'Resent email validation code to ' + principal.email);

    return Promise.resolve();
  }

  /**
   * Change the password for a logged in user verifying old password
   * Also called from login on a force change password so technically you don't have to be logged in
   */
  @remote({
    validate: function () {
      return this.validate(document.getElementById('changePasswordFields'));
    },
  })
  changePassword(page) {
    if (!this.loggedIn) {
      throw { code: 'not_loggedin', text: 'Not logged in' };
    }
    return this.changePasswordForPrincipal(this.getPrincipal(), page);
  }

  changePasswordForPrincipal(principal, page?) {
    return principal.authenticate(this.password, true).then(
      function () {
        return principal
          .establishPassword(
            this.newPassword,
            passwordExpiresMinutes.call(this)
              ? new Date(new Date().getTime() + passwordExpiresMinutes.call(this) * 1000 * 60)
              : null
          )
          .then(
            function () {
              log.call(this, 'Changed password for ' + principal.email);
              if (this.sendEmail) {
                this.sendEmail('password_changed', principal.email, principal.firstName, [
                  { name: 'firstName', content: principal.firstName },
                ]);
              }

              return page ? this.setPage(page) : Promise.resolve(true);
            }.bind(this)
          );
      }.bind(this)
    );
  }

  /**
   * Request that an email be sent with a password change link
   */
  @remote({
    validate: function () {
      return this.validate(document.getElementById('publicResetPasswordFields'));
    },
  })
  publicResetPassword(url, channel, page?) {
    url = urlparser.parse(url, true);
    log.call(this, 'Request password reset for ' + this.email);
    return this.triggerResetPassword(this.email, url, channel, page);
  }

  private async triggerResetPassword(email: string, url, channel: string, page?: string): Promise<void> {
    const emailChangeUrl = url.protocol + '//' + url.host.replace(/:.*/, '') + (url.port > 1000 ? ':' + url.port : '');
    await EmailerUtils.triggerResetPasswordEmail(email, channel, emailChangeUrl);

    if (page) {
      this.setPage(page);
    }
  }

  /**
   * Change the password given the token and log the user in
   * Token was generated in publicResetPassword and kept in principal entity to verify
   */
  @remote({
    validate: function () {
      return this.validate(document.getElementById('publicChangePasswordFromTokenFields'));
    },
  })
  publicChangePasswordFromToken(page?) {
    let principal;
    const caseRegEx = new RegExp('^' + this.email.toLowerCase().replace(/([^0-9a-zA-Z])/g, '\\$1') + '$');

    return this.getPrincipal()
      .amorphicClass.getFromPersistWithQuery(
        queryFilter.call(this, { email: { $regex: caseRegEx, $options: 'i' } }),
        null,
        null,
        null,
        true
      )
      .then(
        function (principals) {
          if (principals.length < 1) {
            throw {
              code: 'invalid_password_change_token',
              text: 'Invalid password change link - make sure you copied correctly from the email',
            };
          }

          principal = principals[0];
          return principal.consumePasswordChangeToken(this.passwordChangeHash, this.newPassword);
        }.bind(this)
      )
      .then(
        function () {
          return this.getPrincipal().amorphicClass.persistorFetchById(principal._id);
        }.bind(this)
      )
      .then(
        function (p) {
          principal = p;
          return principal.establishPassword(this.newPassword);
        }.bind(this)
      )
      .then(
        function () {
          this.setLoggedInState(principal);
          this.amorphicate(principal);

          log.call(this, 'Changed password for ' + principal.email);
          if (this.sendEmail) {
            this.sendEmail('password_changed', principal.email, principal.firstName, [
              { name: 'firstName', content: principal.firstName },
            ]);
          }

          return page ? this.setPage(page) : Promise.resolve(true);
        }.bind(this)
      );
  }

  /**
   * Verify the email code
   */
  @remote()
  publicVerifyEmailFromCode(page?): Promise<boolean> {
    let principal;
    return this.getPrincipal()
      .amorphicClass.persistorFetchByQuery(
        queryFilter.call(this, {
          email: {
            $regex: new RegExp('^' + this.email.toLowerCase().replace(/([^0-9a-zA-Z])/g, '\\$1') + '$'),
            $options: 'i',
          },
        }),
        {
          transient: true,
        }
      )
      .then(
        function (principals) {
          if (principals.length < 1) {
            throw {
              code: 'invalid_email_verification_code',
              text: 'Invalid verification link - make sure you copied correctly from the email',
            };
          }

          principal = principals[0];
          return principal.consumeEmailVerificationCode(this.verifyEmailCode);
          this.amorphicate(principal);
        }.bind(this)
      )
      .then(
        function () {
          return page ? this.setPage(page) : Promise.resolve(true);
        }.bind(this)
      );
  }

  /**
   * Verify the email code assuming principal already in controller
   */
  @remote()
  privateVerifyEmailFromCode(verifyEmailCode: string): Promise<boolean> {
    const principal = this.getPrincipal();
    try {
      return principal.consumeEmailVerificationCode(verifyEmailCode);
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  @serverOnly()
  async elasticPut(policy: Policy, reindex?: boolean, cp?: boolean): Promise<void> {
    if ((this.elasticSend && policy) || reindex) {
      try {
        let primaryAdvisor: AdvisorInfoElastic = null;
        const otherOwner: PersonElastic = await getOtherOwner(policy);

        if (cp) {
          primaryAdvisor = agentToAdvisor(policy.application, policy.primaryPolicyAgent);
        }
        if (!policy.insuredType || policy.insuredType === 'you') {
          if (reindex) {
            await this.elasticReindexServer(policy.customer, policy, 'Owner/Insured', null, otherOwner, primaryAdvisor);
          } else {
            await this.publicElasticPutServer(
              policy.customer,
              policy.customer.email,
              policy,
              'Owner/Insured',
              null,
              otherOwner,
              primaryAdvisor
            );
          }
        } else {
          //do include insured person
          const insured: PersonElastic = await getInsured(policy);

          if (reindex) {
            await this.elasticReindexServer(policy.customer, policy, 'Owner', insured, otherOwner, primaryAdvisor);
          } else {
            await this.publicElasticPutServer(
              policy.customer,
              policy.customer.email,
              policy,
              'Owner',
              insured,
              otherOwner,
              primaryAdvisor
            );
          }
        }
      } catch (e) {
        LoggerSingleton.getInstance().error(
          {
            module: 'AuthenticatingController',
            function: 'elasticPut',
            category: Category.request,
            error: {
              name: e?.name,
              message: e?.message,
              stack: e?.stack,
              isHumanRelated: false,
              data: {
                policyId: policy._id,
                policyNumber: policy.policyNumber,
              },
            },
          },
          'Unable to collect data for elasticsearch'
        );
      }
    }
  }

  @remote({ on: 'server' })
  async publicElasticPutServer(
    customer: Customer,
    email: string,
    policy: Policy,
    role: string,
    insured,
    otherOwner,
    advisor: AdvisorInfoElastic
  ): Promise<void> {
    if (
      !this.amorphic.config ||
      !this.amorphic.config.nconf.get('summaryPublisherEnabled') ||
      (customer.policies[0] && customer.policies[0].policyNumber)
    ) {
      return;
    }

    const owner = await getOwner(policy, role);

    const advisorList: AdvisorInfoElastic[] = getAdditionalAdvisors(policy);

    let emailSearch;
    if (email) {
      emailSearch = email.replace(/[^a-zA-Z0-9]/g, '');
    }

    const customerId = customer._id;

    const juvieSigner = await getJuvieSigner(policy);

    const elasticData = {
      eventType: 'summary',
      id: customerId,
      customerId: customerId,
      firstName: owner.firstName,
      lastName: owner.lastName,
      middleName: owner.middleName,
      email: email,
      emailSearch: emailSearch,
      dob: owner.dob,
      role: role,
      ssn: owner.ssn,
      ssnLastFour: owner.ssnLastFour,
      state: owner.state,
      covered: insured,
      otherOwner: otherOwner,
      channel: policy.getChannelCode(),
      advisor: advisor,
      advisorList: advisorList,
      ownerClassification: customer.application.primaryPolicy.ownerClassification,
      trustOrEntity: customer.application.primaryPolicy.trustOrOtherEntity
        ? customer.application.primaryPolicy.trustOrOtherEntity.type
        : null,
      name: null,
      govtId: null,
      applicationId: policy.application._id,
      usesUnderwriting: policy.usesUnderwriting(),
      juvieSigner: juvieSigner,
      product: policy.product,
    };
    if (policy.ownerEntity) {
      elasticData.name = policy.ownerEntity.getFullNameOfOwner();
      elasticData.govtId = policy.ownerEntity.taxId || '';
      elasticData.state = policy.ownerEntity.trustSitus || '';
      elasticData.dob = null;
      elasticData.firstName = null;
      elasticData.middleName = null;
      elasticData.lastName = null;
      elasticData.ssn = 'None';
    }

    LoggerSingleton.getInstance().info(
      {
        module: 'AuthenticatingController',
        function: 'publicElasticPutServer',
        category: Category.request,
        data: {
          policyNumber: policy.policyNumber,
          AdvisorCount: advisorList.length,
        },
      },
      `Additional advisor list`
    );

    return RabbitmqPublisher.singleton().publish(elasticData, ElasticSchema);
  }

  @remote({ on: 'server' })
  async elasticReindexServer(
    customer,
    policy: Policy,
    role: string,
    insured,
    otherOwner,
    advisor: AdvisorInfoElastic
  ): Promise<void> {
    if (!this.amorphic.config || !this.amorphic.config.nconf.get('summaryPublisherEnabled')) {
      return;
    }

    const owner: PersonElastic = await getOwner(policy, role);

    const customerId = customer.primaryCustomer.customerPersistor.id;

    const policyNum = policy.policyNumber;

    const juvieSigner = await getJuvieSigner(policy);

    const elasticData = {
      eventType: 'summary',
      id: policyNum,
      customerId: customerId,
      firstName: owner.firstName,
      lastName: owner.lastName,
      middleName: owner.middleName,
      email: owner.email,
      emailSearch: owner.emailSearch,
      dob: owner.dob,
      role: role,
      ssn: owner.ssn,
      ssnLastFour: owner.ssnLastFour,
      state: owner.state,
      policy: policy.policyNumber,
      policyLastFour: policy.policyNumber.slice(-4),
      channel: policy.getChannelCode(),
      covered: insured,
      otherOwner: otherOwner,
      advisor: advisor,
      submitted: policy.submittedAt,
      ownerClassification: policy.ownerClassification,
      trustOrEntity: policy.trustOrOtherEntity ? policy.trustOrOtherEntity.type : null,
      govtId: null,
      name: null,
      applicationId: policy.application._id,
      juvieSigner: juvieSigner,
      product: policy.product,
    };
    if (policy.ownerEntity) {
      elasticData.name = policy.ownerEntity.getFullNameOfOwner();
      elasticData.govtId = policy.ownerEntity.taxId || '';
      elasticData.state = policy.ownerEntity.trustSitus || '';
      elasticData.dob = null;
      elasticData.firstName = null;
      elasticData.middleName = null;
      elasticData.lastName = null;
      elasticData.ssn = 'None';
    }
    RabbitmqPublisher.singleton().publish(elasticData, ElasticSchema);

    const elasticDelete = {
      eventType: 'summary',
      channel: policy.getChannelCode(),
      id: customerId,
      delete: true,
    };
    return RabbitmqPublisher.singleton().publish(elasticDelete, ElasticSchema);
  }

  @serverOnly()
  async preServerCall(
    hasChanges: boolean,
    _changes: PreServerCallChanges,
    _context: CallContext,
    forceUpdate: undefined | boolean,
    functionName: string,
    remoteCall: RemoteCall,
    isPublic: boolean,
    HTTPObjs: HTTPObjs
  ) {
    this.elasticSend = false;
    for (const property in _changes) {
      if (
        property.toString() === 'Person' ||
        property.toString() === 'Policy' ||
        property.toString() === 'Address' ||
        property.toString() === 'Entity'
      ) {
        this.elasticSend = true;
      }
    }
  }

  @serverOnly()
  serverInit(): void {
    if (this.amorphic.config) {
      const rabbitmqHitUserEventsConfig = this.amorphic.config.nconf.get('rabbitmqHitUserEventsConfig');
      rabbitmqHitUserEventsConfig.host = this.amorphic.config.nconf.get('RABBIT_HOST');
      rabbitmqHitUserEventsConfig.port = this.amorphic.config.nconf.get('RABBIT_PORT');
      rabbitmqHitUserEventsConfig.user = this.amorphic.config.nconf.get('RABBIT_USERNAME');
      rabbitmqHitUserEventsConfig.pswd = this.amorphic.config.nconf.get('RABBIT_PASSWORD');
      rabbitmqHitUserEventsConfig.secure = this.amorphic.config.nconf.get('RABBIT_SECURE') || false;

      RabbitmqPublisher.singleton().setMQConfig(rabbitmqHitUserEventsConfig);
      RabbitmqPublisher.singleton().start();
    }
  }

  resolveProp(propStr: string, loopIndex?: number) {
    let prop = this;
    if (!isNaN(loopIndex)) {
      propStr = propStr.replace('[i]', '.' + loopIndex + '');
    }
    const names = propStr.split('.');
    names.forEach((p) => {
      try {
        prop = prop[p];
      } catch (e) {
        prop = null;
      }
    });
    return prop;
  }

  @remote({ on: 'server' })
  public async checkIfAccountVerified(): Promise<boolean> {
    let isVerified = false;

    if (this.isAccountVerified) {
      isVerified = true;
    } else {
      if (this.isAuth0Enabled) {
        const accountInfo: Auth0ResponseAccount = await Auth0Client.getAccountInfo(this.email);
        if (accountInfo) {
          isVerified = accountInfo.email_verified;
        }
      }
    }

    return isVerified;
  }

  @remote({ on: 'server' })
  public async getIsAuth0Enabled(): Promise<boolean> {
    if (this.isAuth0Enabled === null) {
      this.isAuth0Enabled = await this.lookUpIsAuth0Enabled();
    }

    return this.isAuth0Enabled;
  }

  public async lookUpIsAuth0Enabled(): Promise<boolean> {
    return false;
  }

  @remote({ on: 'server', public: true })
  public async resolveAuth0Data(): Promise<void> {
    // NO-OP
  }
}

export interface PreRegistrationVerificationResult {
  error: string;
  code: string;
  text: string;
}
